import type React from 'react';
import { useTranslation } from 'react-i18next';
import { FieldError, Label, NumberField } from '@heroui/react';
import type { SalesTransactionDiscountType } from '@/api/models';
import { getNodeTestIdProp } from '@/utils';
import { formatGel } from '../../sellerMeta';
import { DISCOUNT_FIELD_FORMAT } from './discountDialogMeta';
import type { DiscountDialogProblem } from './useDiscountDialog';

type DiscountDialogValueFieldProps = {
    type: SalesTransactionDiscountType;
    /** Remounts the field whenever it must start over (a new opening, a type switch). */
    fieldKey: string;
    fieldDefaultValue: number | undefined;
    onValueText: (text: string) => void;
    problem: DiscountDialogProblem | null;
    valueInputRef: React.RefObject<HTMLInputElement | null>;
};

/** The inline message for a value that cannot be used, naming its limit. */
const problemMessage = (
    problem: DiscountDialogProblem,
    t: (key: string, options?: Record<string, unknown>) => string,
): string => {
    switch (problem.kind) {
        case 'valueInvalid':
            return t(
                problem.type === 'percentage'
                    ? 'seller.discount.valueInvalidPercentage'
                    : 'seller.discount.valueInvalidFixed',
            );
        case 'lineTooLarge':
            return t('seller.discount.lineTooLarge', {
                max: formatGel(problem.max),
            });
        case 'saleTooLarge':
            return t('seller.discount.saleTooLarge', {
                max: formatGel(problem.max),
            });
        case 'nothingLeftToPay':
            return t('seller.discount.nothingLeftToPay');
    }
};

/** The keys react-aria's number field spins its value with. */
const SPIN_KEYS = new Set([
    'ArrowUp',
    'ArrowDown',
    'PageUp',
    'PageDown',
    'Home',
    'End',
]);

/**
 * The discount's value (render-only): a real `NumberField` in the type's
 * format. Its keystrokes are the value (`onValueText`); the field's commit,
 * which rounds to its format, is not listened to, so `Enter` can never apply
 * a rounded version of a refused value. Spinning would change the value
 * without a keystroke, so it is off: the wheel, and the spin keys (stopped
 * before react-aria sees them; the caret still moves). A value that cannot be
 * used marks the field invalid and names its limit underneath.
 */
export const DiscountDialogValueField: React.FC<
    DiscountDialogValueFieldProps
> = ({
    type,
    fieldKey,
    fieldDefaultValue,
    onValueText,
    problem,
    valueInputRef,
}) => {
    const { t } = useTranslation('pages');

    return (
        <NumberField
            defaultValue={fieldDefaultValue}
            formatOptions={DISCOUNT_FIELD_FORMAT[type]}
            fullWidth
            isInvalid={problem !== null}
            isWheelDisabled
            key={fieldKey}
            minValue={0}
            validationBehavior="aria"
        >
            <Label>{t('seller.discount.value')}</Label>
            <NumberField.Group>
                <NumberField.Input
                    className="w-full tabular-nums"
                    onInput={(event) => onValueText(event.currentTarget.value)}
                    onKeyDownCapture={(event) => {
                        if (SPIN_KEYS.has(event.key)) {
                            event.stopPropagation();
                        }
                    }}
                    ref={valueInputRef}
                    {...getNodeTestIdProp('seller-discount-value')}
                />
            </NumberField.Group>
            {problem ? (
                <FieldError {...getNodeTestIdProp('seller-discount-problem')}>
                    {problemMessage(problem, t)}
                </FieldError>
            ) : null}
        </NumberField>
    );
};
